import {
    IRowRenderType,
    isTonic,
    normaliseTempo,
    NotationContent,
    Tempo,
} from "../model";
import { mapRow } from "./internal";

/**
 * Operations on `FileOptions`.
 *
 * Exported as `trackOps` because tracks were all this module held when it was
 * written; it now also owns the file-level tempo. The name is kept rather than
 * churned through every call site.
 *
 * A track is either *generic* (file-wide, in `fileOptions.subRowNames`) or
 * *row-specific* (an extra track in `row.subRowNames`). The `genericSubRow`
 * flag selects which, mirroring the existing call sites.
 *
 * These are the operations that made purity load-bearing: the old code mutated
 * `fileOptions` in place, and because the parse step only shallow-copied, that
 * object was shared with the page's `fileDocument` state. `FileSummaryHeader`
 * silently depended on that aliasing to see updates. It now subscribes to
 * published state instead — see the note in that component.
 */

/**
 * Whether a file-wide track can take this name.
 *
 * Replaces a `throw` inside `setSubRowName`. Using an exception for a rule the
 * UI can check up-front meant control flow ran through a catch block, and any
 * caller that forgot to wrap the call would crash. The dialog now asks first.
 */
export function canRenameTrack(
    content: NotationContent,
    args: { name: string; subRowIndex: number }
): boolean {
    const existingIndex = content.fileOptions.subRowNames.indexOf(args.name);
    // Free if unused, or if it is already this track's own name.
    return existingIndex === -1 || existingIndex === args.subRowIndex;
}

export function setSubRowName<T extends NotationContent>(
    content: T,
    args: {
        subRowIndex: number;
        genericSubRow: boolean;
        name: string;
        sectionIndex: number;
        rowIndex: number;
    }
): T {
    const { subRowIndex, genericSubRow, name, sectionIndex, rowIndex } = args;

    if (genericSubRow) {
        // No longer throws: a duplicate is simply rejected. The dialog checks
        // `canRenameTrack` first and shows the message itself.
        if (!canRenameTrack(content, { name, subRowIndex })) {
            return content;
        }
        const previous = content.fileOptions.subRowNames[subRowIndex];
        const subRowNames = content.fileOptions.subRowNames.map((n, i) =>
            i === subRowIndex ? name : n
        );
        return {
            ...content,
            fileOptions: {
                ...content.fileOptions,
                subRowNames,
                // The summary track is identified by name, so a rename has to
                // follow it or the summary silently detaches.
                summarySubRow:
                    content.fileOptions.summarySubRow === previous
                        ? name
                        : content.fileOptions.summarySubRow,
            },
        };
    }

    return mapRow(content, sectionIndex, rowIndex, (row) => ({
        ...row,
        subRowNames: row.subRowNames.map((n, i) =>
            i === subRowIndex ? name : n
        ),
    }));
}

export function getSubRowName(
    content: NotationContent,
    args: {
        subRowIndex: number;
        genericSubRow: boolean;
        sectionIndex: number;
        rowIndex: number;
    }
): string {
    return args.genericSubRow
        ? content.fileOptions.subRowNames[args.subRowIndex]
        : content.sections[args.sectionIndex].rows[args.rowIndex].subRowNames[
              args.subRowIndex
          ];
}

/** Sets which track feeds the summary header. Unknown names are ignored. */
export function setSummaryTrackName<T extends NotationContent>(
    content: T,
    args: { trackName: string }
): T {
    if (content.fileOptions.subRowNames.indexOf(args.trackName) === -1) {
        return content;
    }
    return {
        ...content,
        fileOptions: { ...content.fileOptions, summarySubRow: args.trackName },
    };
}

/**
 * Sets — or clears — the tempo for the whole composition.
 *
 * The value is normalised here rather than at the call site so that every
 * route into the document (the editor, a future import, a test) produces the
 * one canonical shape. An unusable `bpm` degrades to `null` and keeps the
 * marking; a tempo carrying nothing at all becomes `null`, so "unset" cannot
 * be stored three different ways.
 *
 * NOTE: this is the FILE tempo. `Section.tempo` exists and round-trips, but
 * has no operation yet on purpose — there is no section-tempo UI, and adding
 * one before the shape is exercised would be guessing at it.
 */
export function setFileTempo<T extends NotationContent>(
    content: T,
    args: { tempo: Tempo | null }
): T {
    return {
        ...content,
        fileOptions: {
            ...content.fileOptions,
            tempo: normaliseTempo(args.tempo),
        },
    };
}

/**
 * Sets — or clears — the western note Sa is tuned to.
 *
 * VALIDATED, NEVER TRUSTED. The argument is typed `string` on purpose: what
 * reaches this is a picker's value, a stored document, or an HTTP body, and
 * none of those were checked by a compiler. An unrecognised name would rotate
 * the chromatic table off its end and mislabel every key on the keyboard — and,
 * once an export reads this, transpose the whole composition.
 *
 * An unrecognised tonic leaves the document UNCHANGED, matching
 * `setSummaryTrackName` above. Clearing on a typo would throw away a tonic the
 * user had already chosen, which is a worse outcome than ignoring the keypress.
 * `null` clears, explicitly and only.
 *
 * NOTE: this is the seam. There is no tonic picker yet — the read path seeds
 * the editor from the document and nothing writes back — so this exists so that
 * shipping one is a UI change alone. `web/src/store/middleware/notationSaveListener.ts`
 * must list the corresponding action in `MUTATION_ACTIONS` at that point, or a
 * tonic change will not queue a save.
 */
export function setScaleTonic<T extends NotationContent>(
    content: T,
    args: { tonic: string | null }
): T {
    if (args.tonic !== null && !isTonic(args.tonic)) {
        return content;
    }
    return {
        ...content,
        fileOptions: { ...content.fileOptions, scaleTonic: args.tonic },
    };
}

export function setTrackType<T extends NotationContent>(
    content: T,
    args: {
        sectionIndex: number;
        rowIndex: number;
        subRowIndex: number;
        genericSubRow: boolean;
        type: IRowRenderType;
    }
): T {
    const { sectionIndex, rowIndex, subRowIndex, genericSubRow, type } = args;

    if (genericSubRow) {
        return {
            ...content,
            fileOptions: {
                ...content.fileOptions,
                subRowRenderTypes: content.fileOptions.subRowRenderTypes.map(
                    (t, i) => (i === subRowIndex ? type : t)
                ),
            },
        };
    }

    return mapRow(content, sectionIndex, rowIndex, (row) => ({
        ...row,
        renderMethod: row.renderMethod.map((t, i) =>
            i === subRowIndex ? type : t
        ),
    }));
}
